// Tenant branding contract for the portal, served on `GET /v1/{slug}/brand`.
// The brand fields (logo/favicon/touchIcon/brandColor) are parsed defensively
// so the portal degrades to its brand-name/default styling when a field is
// unset.
//
// `brandColor` is a single HEX value (the Beacon model): the theming layer runs
// it through hsds `makeBrandColors` to derive a contrast-safe palette, rather
// than carrying per-element colors. See INB-4470.

export interface PortalBranding {
  brandName: string
  logoUrl?: string
  faviconUrl?: string
  touchIconUrl?: string
  /** Single brand HEX, e.g. `#7E80E7`. Fed to `makeBrandColors`. */
  brandColor?: string
  /** Whether the "Powered by Help Scout" badge shows. Defaults to true. */
  poweredBy: boolean
}

const HEX_PATTERN = /^#?([0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i

const asString = (value: unknown): string | undefined => {
  if (typeof value !== 'string') return undefined
  const trimmed = value.trim()
  return trimmed !== '' ? trimmed : undefined
}

// Coerce to a leading `#` so the value is always valid input for hsds
// `makeBrandColors`, which requires a `#`-prefixed hex.
const asHexColor = (value: unknown): string | undefined => {
  if (typeof value !== 'string') return undefined
  const trimmed = value.trim()
  if (!HEX_PATTERN.test(trimmed)) return undefined
  return trimmed.startsWith('#') ? trimmed : `#${trimmed}`
}

/**
 * Single bridge typecast for the `/v1/{slug}/brand` response. Invalid or
 * absent fields are dropped so the theming layer can fall back to default
 * Help Scout styling.
 */
export const normalizeBranding = (raw: unknown): PortalBranding => {
  const record = (raw ?? {}) as Record<string, unknown>
  return {
    brandName: asString(record.name) ?? '',
    logoUrl: asString(record.logoUrl),
    faviconUrl: asString(record.faviconUrl),
    touchIconUrl: asString(record.touchIconUrl),
    brandColor: asHexColor(record.brandColor),
    poweredBy: record.poweredBy !== false,
  }
}
